import { useEffect, useRef, useState } from 'preact/hooks';
import { ApiError, type Api } from '../api.js';
import { when } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { navigate } from '../router.js';
import { Callout, Dialog, LoadError, Loading, MoreOptions, PageHead, toast } from '../ui.js';

/**
 * The film archive (owner 2026-10-17, step 1): every film KFF has delivered, read from the client folders in Google
 * Drive, searchable and playable in one place. Films play in Drive's own player; nothing is copied. Internal only:
 * KFF employees see it, freelancers and clients never do.
 */

interface FilmCard {
  key: string;
  name: string;
  client: { id: string | null; name: string };
  project: { id: string | null; name: string } | null;
  year: number | null;
  month: number | null;
  durationS: number | null;
  formats: string[];
  thumb: string | null;
  hidden: boolean;
  unmatched: boolean;
}
interface ScanStatus {
  started: boolean;
  phase: 'idle' | 'seed' | 'walk';
  firstRun: boolean;
  finishedAt: string | null;
  lastStepAt: string | null;
  error?: string | null;
  driveUser?: string | null;
}
interface ArchiveView {
  total: number;
  all: number;
  films: FilmCard[];
  facets: { clients: { id: string; name: string; count: number }[]; years: { year: number; count: number }[]; formats: { format: string; count: number }[] };
  counts: { hidden: number; review: number } | null;
  scan: ScanStatus;
  can: { manage: boolean; play: boolean };
}
interface FilmFormat {
  fileId: string;
  format: string | null;
  name: string;
  width: number | null;
  height: number | null;
  durationS: number | null;
  size: number | null;
  embedUrl: string;
  driveUrl: string;
}
interface FilmDetail extends Omit<FilmCard, 'formats'> {
  formats: FilmFormat[];
  folderUrl: string;
  productionType: string | null;
  creativePackage: string | null;
  deliveredAt: string | null;
}

type View = 'all' | 'review' | 'hidden';
type Length = '' | 'short' | 'medium' | 'long';

const TX = texts(
  {
    title: 'Film archive',
    sub: 'Every film we have delivered, from the client folders in Drive.',
    search: 'Search',
    searchPh: 'Film, client or project',
    formats: 'Format',
    allFormats: 'All formats',
    client: 'Client',
    year: 'Year',
    length: 'Length',
    all: 'All',
    lengths: { short: 'Under 30 s', medium: '30–90 s', long: 'Over 90 s' } as Record<Exclude<Length, ''>, string>,
    show: 'Show',
    views: (c: { hidden: number; review: number }) => ({ all: 'All films', review: `To review (${c.review})`, hidden: `Hidden films (${c.hidden})` }) as Record<View, string>,
    reviewInfo: 'Films whose client folder matches no client in the Hub. They are in the archive as usual; the client is the folder name.',
    nFilms: (n: number) => `${n} ${n === 1 ? 'film' : 'films'}`,
    nOf: (n: number, all: number) => `${n} of ${all} films`,
    clear: 'Clear filters',
    none: 'No films match. Try fewer words or clear the filters.',
    showMore: 'Show more',
    notStarted: 'The archive has not been read from Drive yet',
    notStartedAdmin: 'The Hub reads the client folder under every "{Client} - General" folder in the shared drives, never Internal or RAW, back to 2021. It only reads: nothing in Drive changes. Keep this page open until it is done; after that new films come in by themselves every few minutes.',
    notStartedStaff: 'An admin starts it once; after that new films come in by themselves.',
    readDrive: 'Read from Drive',
    continueRead: 'Continue reading',
    reading: (files: number) => `Reading Drive… ${files} video files so far`,
    firstRun: 'The first reading is not finished, so some films are still missing.',
    updated: (at: string) => `Updated from Drive ${at}`,
    driveError: 'The last reading from Drive failed',
    driveSkipped: 'Drive would not list some folders, so they were left out. The nightly reading tries them again.',
    play: (name: string) => `Play ${name}`,
    duration: (s: number) => (s < 60 ? `${s} s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`),
    // the player
    format: 'Format',
    openDrive: 'Open in Drive',
    openFolder: 'Open folder',
    openProject: 'Open project',
    hide: 'Hide film',
    unhide: 'Show film again',
    hidden: 'Film hidden from the archive',
    shown: 'Film back in the archive',
    close: 'Close',
    file: 'File',
    resolution: 'Resolution',
    size: 'Size',
    delivered: 'In Drive since',
    projectType: 'Production',
    noPlay: 'Plays when you are signed in to Google with your KFF account.',
    // the client card
    films: 'Films',
    allFilms: (n: number) => `All ${n} in the film archive`,
    noFilms: 'No films from this client in the archive yet.'
  },
  {
    title: 'Filmarkiv',
    sub: 'Alla filmer vi har levererat, från kundmapparna i Drive.',
    search: 'Sök',
    searchPh: 'Film, kund eller projekt',
    formats: 'Format',
    allFormats: 'Alla format',
    client: 'Kund',
    year: 'År',
    length: 'Längd',
    all: 'Alla',
    lengths: { short: 'Under 30 s', medium: '30–90 s', long: 'Över 90 s' },
    show: 'Visa',
    views: (c) => ({ all: 'Alla filmer', review: `Att granska (${c.review})`, hidden: `Dolda filmer (${c.hidden})` }),
    reviewInfo: 'Filmer vars kundmapp inte matchar någon kund i Hubben. De finns i arkivet som vanligt; kunden är mappens namn.',
    nFilms: (n) => `${n} ${n === 1 ? 'film' : 'filmer'}`,
    nOf: (n, all) => `${n} av ${all} filmer`,
    clear: 'Rensa filter',
    none: 'Inga filmer matchar. Prova färre ord eller rensa filtren.',
    showMore: 'Visa fler',
    notStarted: 'Arkivet har inte lästs in från Drive än',
    notStartedAdmin: 'Hubben läser kundmappen under varje "{Kund} - General" i de delade enheterna, aldrig Internal eller RAW, tillbaka till 2021. Den bara läser: inget i Drive ändras. Ha sidan öppen tills det är klart; sedan kommer nya filmer in av sig själva med några minuters mellanrum.',
    notStartedStaff: 'En admin startar det en gång; sedan kommer nya filmer in av sig själva.',
    readDrive: 'Läs in från Drive',
    continueRead: 'Fortsätt läsa',
    reading: (files) => `Läser Drive… ${files} videofiler hittills`,
    firstRun: 'Första inläsningen är inte klar, så några filmer saknas fortfarande.',
    updated: (at) => `Uppdaterat från Drive ${at}`,
    driveError: 'Senaste läsningen från Drive misslyckades',
    driveSkipped: 'Drive kunde inte lista några mappar, så de hoppades över. Nattens inläsning försöker igen.',
    play: (name) => `Spela upp ${name}`,
    duration: (s) => (s < 60 ? `${s} s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`),
    format: 'Format',
    openDrive: 'Öppna i Drive',
    openFolder: 'Öppna mappen',
    openProject: 'Öppna projektet',
    hide: 'Dölj film',
    unhide: 'Visa filmen igen',
    hidden: 'Filmen dold i arkivet',
    shown: 'Filmen tillbaka i arkivet',
    close: 'Stäng',
    file: 'Fil',
    resolution: 'Upplösning',
    size: 'Storlek',
    delivered: 'I Drive sedan',
    projectType: 'Produktion',
    noPlay: 'Spelas när du är inloggad i Google med ditt KFF-konto.',
    films: 'Filmer',
    allFilms: (n) => `Alla ${n} i filmarkivet`,
    noFilms: 'Inga filmer från kunden i arkivet än.'
  }
);

const MONTHS: Record<Lang, string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  sv: ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']
};
const dateOf = (f: { year: number | null; month: number | null }, lang: Lang) => (f.year ? `${f.month ? `${MONTHS[lang][f.month - 1]} ` : ''}${f.year}` : '');
/** 16x9 → 16:9, as people say it */
const fmt = (x: string) => x.replace('x', ':');
const mb = (n: number | null) => (n === null ? '' : n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : `${Math.round(n / 1e6)} MB`);
const go = (path: string) => (e: Event) => (e.preventDefault(), navigate(path));
const val = (e: Event) => (e.currentTarget as HTMLInputElement).value;
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

interface Filters {
  q: string;
  format: string;
  client: string;
  year: string;
  length: Length;
  view: View;
}
const EMPTY: Filters = { q: '', format: '', client: '', year: '', length: '', view: 'all' };
const PAGE = 48;

function queryOf(f: Filters, limit: number): string {
  const s = new URLSearchParams();
  if (f.q.trim()) s.set('q', f.q.trim());
  if (f.format) s.set('format', f.format);
  if (f.client) s.set('client', f.client);
  if (f.year) s.set('year', f.year);
  if (f.length) s.set('length', f.length);
  if (f.view === 'hidden') s.set('hidden', '1');
  if (f.view === 'review') s.set('review', '1');
  s.set('limit', String(limit));
  return s.toString();
}

/** A film's card: still, length, name, client and month, formats. */
function Card(p: { f: FilmCard; lang: Lang; onOpen: () => void; compact?: boolean }) {
  const t = TX[p.lang];
  const [broken, setBroken] = useState(false);
  return (
    <li class={`fa-card${p.f.hidden ? ' is-hidden' : ''}`}>
      <button type="button" class="fa-open" onClick={p.onOpen} aria-label={t.play(p.f.name)}>
        <span class="fa-thumb">
          {broken || !p.f.thumb ? <span class="fa-noimg" aria-hidden="true" /> : <img src={p.f.thumb} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} />}
          {p.f.durationS ? <span class="fa-dur">{t.duration(p.f.durationS)}</span> : null}
        </span>
        <span class="fa-body">
          <span class="fa-name">{p.f.name}</span>
          <span class="small muted">
            {p.compact ? dateOf(p.f, p.lang) : [p.f.client.name, dateOf(p.f, p.lang)].filter(Boolean).join(' · ')}
          </span>
          {!p.compact && p.f.formats.length ? (
            <span class="fa-formats">
              {p.f.formats.map((x) => (
                <span class="fa-format">{fmt(x)}</span>
              ))}
            </span>
          ) : null}
        </span>
      </button>
    </li>
  );
}

/** The player: Drive's own, one format at a time, with the facts and the way to Drive and the project. */
function Player(p: { api: Api; filmKey: string; manage: boolean; onClose: () => void; onChanged: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [f, setF] = useState<FilmDetail | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    p.api.get<FilmDetail>(`/api/archive/films/${encodeURIComponent(p.filmKey)}`).then(setF, setErr);
  }, [p.filmKey]);
  const cur = f?.formats[i] ?? f?.formats[0];
  const tall = Boolean(cur && cur.width && cur.height && cur.height > cur.width);
  const square = Boolean(cur && cur.width && cur.height && cur.height === cur.width);
  const toggle = async () => {
    if (!f) return;
    setBusy(true);
    try {
      await p.api.put(`/api/archive/films/${encodeURIComponent(f.key)}/hidden`, { hidden: !f.hidden });
      toast({ text: f.hidden ? t.shown : t.hidden });
      p.onChanged();
      p.onClose();
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={f?.name ?? t.title}
      sub={f ? [f.client.name, dateOf(f, lang), f.project?.name && f.project.name !== f.name ? f.project.name : ''].filter(Boolean).join(' · ') : undefined}
      onClose={p.onClose}
      foot={
        <>
          {f && p.manage ? (
            <button type="button" class="btn btn-ghost fa-hide" disabled={busy} onClick={() => void toggle()}>
              {f.hidden ? t.unhide : t.hide}
            </button>
          ) : null}
          {f?.project?.id ? (
            <a class="btn" href={`/projects/${f.project.id}`} onClick={go(`/projects/${f.project.id}`)}>
              {t.openProject}
            </a>
          ) : null}
          {cur ? (
            <a class="btn" href={cur.driveUrl} target="_blank" rel="noopener">
              {t.openDrive}
            </a>
          ) : null}
          <button type="button" class="btn btn-primary" onClick={p.onClose}>
            {t.close}
          </button>
        </>
      }
    >
      {err ? <LoadError error={err} /> : null}
      {!f && !err ? <Loading /> : null}
      {f && cur ? (
        <>
          {f.formats.length > 1 ? (
            <div class="seg" role="group" aria-label={t.format}>
              {f.formats.map((x, n) => (
                <button type="button" class={`seg-btn seg-btn--sm${n === i ? ' is-on' : ''}`} aria-pressed={n === i} onClick={() => setI(n)}>
                  {x.format ? fmt(x.format) : x.name}
                </button>
              ))}
            </div>
          ) : null}
          <div class={`fa-player${tall ? ' fa-player--tall' : square ? ' fa-player--square' : ''}`}>
            <iframe key={cur.fileId} src={cur.embedUrl} title={f.name} allow="autoplay; fullscreen" allowFullScreen loading="lazy" />
          </div>
          <p class="small muted">{t.noPlay}</p>
          <dl class="fa-facts">
            <dt>{t.file}</dt>
            <dd>
              <a href={f.folderUrl} target="_blank" rel="noopener" title={t.openFolder}>
                {cur.name}
              </a>
            </dd>
            {cur.width && cur.height ? (
              <>
                <dt>{t.resolution}</dt>
                <dd>
                  {cur.width} × {cur.height}
                  {cur.size ? ` · ${mb(cur.size)}` : ''}
                </dd>
              </>
            ) : null}
            {f.productionType ? (
              <>
                <dt>{t.projectType}</dt>
                <dd>{[f.productionType, f.creativePackage].filter(Boolean).join(' · ')}</dd>
              </>
            ) : null}
            {f.deliveredAt ? (
              <>
                <dt>{t.delivered}</dt>
                <dd>{new Date(f.deliveredAt).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</dd>
              </>
            ) : null}
          </dl>
        </>
      ) : null}
    </Dialog>
  );
}

/** The admin's "Read from Drive": one step per call until the scan is done, while the page is open. */
function useScan(api: Api, onStep: () => void) {
  const [running, setRunning] = useState(false);
  const [files, setFiles] = useState(0);
  const stop = useRef(false);
  useEffect(() => () => void (stop.current = true), []);
  const run = async (start: boolean) => {
    setRunning(true);
    stop.current = false;
    let first = start;
    try {
      for (let n = 0, tries = 0; n < 500 && !stop.current; n++) {
        let r: { scan: ScanStatus; files: number; step: { error?: string } };
        try {
          r = await api.post('/api/archive/scan', { start: first });
          tries = 0;
        } catch (e) {
          // a busy database or a dropped connection: wait and try the same step again, a few times
          if (!(e instanceof ApiError && (e.status === 503 || e.code === 'bad_response')) || ++tries > 3) throw e;
          await new Promise((ok) => setTimeout(ok, 2000 * tries));
          n--;
          continue;
        }
        first = false;
        setFiles(r.files);
        if (n % 3 === 0 || r.scan.phase === 'idle') onStep();
        if (r.step.error) throw new Error(r.step.error);
        if (r.scan.phase === 'idle') break;
      }
    } catch (e) {
      toast({ text: e instanceof ApiError ? e.message : errText(e) });
    } finally {
      setRunning(false);
      onStep();
    }
  };
  return { running, files, run };
}

export function ArchivePage(p: { api: Api; search?: string }) {
  const lang = useLang();
  const t = TX[lang];
  const [f, setF] = useState<Filters>(() => {
    const q = new URLSearchParams(p.search ?? '');
    return { ...EMPTY, client: q.get('client') ?? '', q: q.get('q') ?? '' };
  });
  const [limit, setLimit] = useState(PAGE);
  const [v, setV] = useState<ArchiveView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [rev, setRev] = useState(0);
  const seq = useRef(0);
  const reload = () => setRev((x) => x + 1);
  const scan = useScan(p.api, reload);
  useEffect(() => {
    const n = ++seq.current;
    const fetchNow = () =>
      p.api.get<ArchiveView>(`/api/archive?${queryOf(f, limit)}`).then(
        (r) => n === seq.current && (setV(r), setErr(null)),
        (e) => n === seq.current && setErr(e)
      );
    if (!v) return void fetchNow();
    // typing waits a moment, so each key press is not a request
    const timer = setTimeout(fetchNow, 250);
    return () => clearTimeout(timer);
  }, [f, limit, rev]);
  if (err && !v) return <LoadError error={err} />;
  if (!v) return <Loading />;
  const set = (patch: Partial<Filters>) => (setF({ ...f, ...patch }), setLimit(PAGE));
  const filtered = Boolean(f.q.trim() || f.format || f.client || f.year || f.length);
  const more = [f.client, f.year, f.length, f.view !== 'all' ? f.view : ''].filter(Boolean).length;
  const manage = v.can.manage;
  const s = v.scan;
  const midScan = s.started && s.phase !== 'idle';
  return (
    <div class="screen">
      <PageHead title={t.title} sub={t.sub} />
      {!s.started ? (
        <Callout tone="info" title={t.notStarted}>
          <p>{manage ? t.notStartedAdmin : t.notStartedStaff}</p>
          {manage ? (
            <p>
              <button type="button" class="btn btn-primary" disabled={scan.running} onClick={() => void scan.run(true)}>
                {scan.running ? t.reading(scan.files) : t.readDrive}
              </button>
            </p>
          ) : null}
        </Callout>
      ) : midScan && s.firstRun ? (
        <Callout tone="info" title={scan.running ? t.reading(scan.files) : t.firstRun}>
          {manage && !scan.running ? (
            <p>
              <button type="button" class="btn btn-primary" onClick={() => void scan.run(false)}>
                {t.continueRead}
              </button>
            </p>
          ) : null}
        </Callout>
      ) : null}
      {manage && s.error ? (
        <Callout tone="warn" title={s.error.startsWith('skipped: ') ? t.driveSkipped : t.driveError}>
          <p class="small">{s.error.replace(/^skipped: /, '')}</p>
        </Callout>
      ) : null}
      {s.started ? (
        <>
          <div class="fa-tools">
            <label class="field fa-search">
              <span class="label">{t.search}</span>
              <input class="input" type="search" value={f.q} placeholder={t.searchPh} onInput={(e) => set({ q: val(e) })} />
            </label>
            {v.facets.formats.length > 1 ? (
              <div class="chips fa-chips" role="group" aria-label={t.formats}>
                <button type="button" class="chip" aria-pressed={!f.format} onClick={() => set({ format: '' })}>
                  {t.allFormats}
                </button>
                {v.facets.formats.slice(0, 6).map((x) => (
                  <button type="button" class="chip" aria-pressed={f.format === x.format} onClick={() => set({ format: f.format === x.format ? '' : x.format })}>
                    {fmt(x.format)}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <MoreOptions id="archive" filled={more} open={more > 0}>
            <div class="fa-more">
              <label class="field">
                <span class="label">{t.client}</span>
                <select class="input" value={f.client} onChange={(e) => set({ client: val(e) })}>
                  <option value="">{t.all}</option>
                  {v.facets.clients.map((c) => (
                    <option value={c.id}>
                      {c.name} ({c.count})
                    </option>
                  ))}
                </select>
              </label>
              <label class="field">
                <span class="label">{t.year}</span>
                <select class="input" value={f.year} onChange={(e) => set({ year: val(e) })}>
                  <option value="">{t.all}</option>
                  {v.facets.years.map((y) => (
                    <option value={String(y.year)}>
                      {y.year} ({y.count})
                    </option>
                  ))}
                </select>
              </label>
              <label class="field">
                <span class="label">{t.length}</span>
                <select class="input" value={f.length} onChange={(e) => set({ length: val(e) as Length })}>
                  <option value="">{t.all}</option>
                  {(['short', 'medium', 'long'] as const).map((x) => (
                    <option value={x}>{t.lengths[x]}</option>
                  ))}
                </select>
              </label>
              {manage && v.counts ? (
                <label class="field">
                  <span class="label">{t.show}</span>
                  <select class="input" value={f.view} onChange={(e) => set({ view: val(e) as View })}>
                    {(['all', 'review', 'hidden'] as const).map((x) => (
                      <option value={x}>{t.views(v.counts!)[x]}</option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          </MoreOptions>
          {f.view === 'review' ? <p class="small muted">{t.reviewInfo}</p> : null}
          <p class="small muted fa-count" aria-live="polite">
            {filtered ? t.nOf(v.total, v.all) : t.nFilms(v.all)}
            {s.finishedAt && !midScan ? ` · ${t.updated(when(s.lastStepAt ?? s.finishedAt, lang))}` : ''}
            {filtered || more ? (
              <>
                {' · '}
                <button type="button" class="btn btn-ghost btn-sm" onClick={() => (setF(EMPTY), setLimit(PAGE))}>
                  {t.clear}
                </button>
              </>
            ) : null}
          </p>
          {v.films.length ? (
            <ul class="fa-grid">
              {v.films.map((x) => (
                <Card key={x.key} f={x} lang={lang} onOpen={() => setOpen(x.key)} />
              ))}
            </ul>
          ) : (
            <p class="muted">{t.none}</p>
          )}
          {v.total > v.films.length ? (
            <p>
              <button type="button" class="btn" onClick={() => setLimit(limit + PAGE)}>
                {t.showMore}
              </button>
            </p>
          ) : null}
        </>
      ) : null}
      {open ? <Player api={p.api} filmKey={open} manage={manage} onClose={() => setOpen(null)} onChanged={reload} /> : null}
    </div>
  );
}

/**
 * The client card's films: the latest few from the archive, and the way to all of them. Shows nothing when the
 * archive is off for the person or has no films from the client.
 */
export function ClientFilms(p: { api: Api; clientId: string }) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<ArchiveView | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    p.api.get<ArchiveView>(`/api/archive?client=${encodeURIComponent(p.clientId)}&limit=4`).then(setV, () => setV(null));
  }, [p.clientId]);
  if (!v || !v.scan.started) return null;
  const href = `/archive?client=${encodeURIComponent(p.clientId)}`;
  return (
    <section class="blk" aria-labelledby="cc-f">
      <h2 class="blk-title" id="cc-f">
        {t.films}
      </h2>
      {v.total ? (
        <>
          <ul class="fa-grid fa-grid--side">
            {v.films.map((x) => (
              <Card key={x.key} f={x} lang={lang} compact onOpen={() => setOpen(x.key)} />
            ))}
          </ul>
          <p>
            <a href={href} onClick={go(href)}>
              {t.allFilms(v.total)}
            </a>
          </p>
        </>
      ) : (
        <p class="muted">{t.noFilms}</p>
      )}
      {open ? <Player api={p.api} filmKey={open} manage={v.can.manage} onClose={() => setOpen(null)} onChanged={() => undefined} /> : null}
    </section>
  );
}

